import { QueryClient, dehydrate, useQuery } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { NextPage, NextPageContext } from "next/types";
import { useEffect, useState } from "react";
import { LoadingOverlay } from "../../components/progress/LoadingOverlay";
import { DropEntry } from "../../dropTypes";
import useAnalytics from "../../hooks/analytics";
import { useAuthState } from "../../hooks/auth";
import { CreatorPageLayoutProps } from "../../layouts/creator/creatorProfile";
import { fetchCreatorBySlug, fetchDropsByCreatorSlug } from "../../lib/backend";
import { ERROR_PAGE } from "../../lib/constants";

const CreatorPageLayout = dynamic<CreatorPageLayoutProps>(() =>
  import("../../layouts/creator/creatorProfile").then(
    (mod) => mod.CreatorPageLayout
  )
);

const ProfilePage: NextPage = () => {
  const analytics = useAnalytics();
  const auth = useAuthState();
  const [isTracked, setIsTracked] = useState<boolean>(false);

  const router = useRouter();
  const slug = router.query.creatorProfile as string;
  const profileQuery = useQuery(["dropsBySlug", slug], () =>
    fetchDropsByCreatorSlug(slug)
  );
  const creatorQuery = useQuery(["creatorBySlug", slug], () =>
    fetchCreatorBySlug(slug)
  );
  const drops: DropEntry[] = profileQuery.data?.filter(
    (drop: DropEntry) => drop.state.kind !== "Draft"
  );

  useEffect(() => {
    if (
      !isTracked &&
      creatorQuery?.data &&
      drops &&
      analytics.isReady &&
      !auth.loading
    ) {
      analytics.trackEvent("viewed_creator_profile_page", {
        authenticated: Boolean(auth.user),
        artist: creatorQuery?.data.slug,
      });
      setIsTracked(true);
    }
  }, [analytics, auth.loading, profileQuery]);

  if (profileQuery.isLoading) {
    return <LoadingOverlay />;
  }

  return <CreatorPageLayout drops={drops} creator={creatorQuery.data} />;
};

ProfilePage.getInitialProps = async (ctx: NextPageContext) => {
  if (ctx.res) {
    const slug = ctx.query.creatorProfile as string;
    const queryClient = new QueryClient();
    const redirect = (location?) => {
      ctx.res.writeHead(307, { Location: location || ERROR_PAGE });
      ctx.res.end();
    };
    try {
      const drops = await fetchDropsByCreatorSlug(slug);
      const creator = await fetchCreatorBySlug(slug);
      if (drops.length < 1) {
        await queryClient.setQueryData(["creatorBySlug", slug], () => creator);
        return { dehydratedState: dehydrate(queryClient) };
      } else if (drops.length === 1) {
        redirect(`/drop/${drops?.[0]?.id}`);
      } else {
        await queryClient.setQueryData(["dropsBySlug", slug], () => drops);
        await queryClient.setQueryData(["creatorBySlug", slug], () => creator);
        return { dehydratedState: dehydrate(queryClient) };
      }
    } catch {
      redirect();
    }
  }

  return {};
};

export default ProfilePage;
